/**
 * Video360 custom element — the per-shape 360° spin, played as video, with
 * drag-to-rotate.
 *
 * The MP4s are the `magicvideos` frame sequences encoded to 24s loops; see
 * docs/diamond-360-video.md. Dragging scrubs `currentTime` rather than stepping
 * frames, so one full drag across the element is one full revolution — the same
 * feel as <diamond-360>, at a fraction of the payload.
 *
 * **Swiper**: rotation and carousel paging share the horizontal axis, so the
 * slide opts out of Swiper entirely (`swiper-no-swiping`) — every drag on the
 * element rotates the stone, and the gallery's navigation arrows (see
 * product-gallery.js) are the way on and off this slide. An earlier design
 * split the gesture by speed (slow drag rotates, quick flick pages the
 * carousel), but with arrows present the flick handoff only made rotation
 * feel unreliable. On desktop the gallery is a grid with no Swiper and every
 * drag simply rotates.
 *
 * @example
 * <video-360 shape="emerald" label="0.18 Carat Emerald Diamond"></video-360>
 * <video-360 src="https://…/x.mp4" poster="https://…/x.jpg"></video-360>
 *
 * @module video-360
 * @version 1.0.0
 * @extends HTMLElement
 */
class Video360 extends HTMLElement {

  /**
   * Shape → 360° video, hosted in Shopify Files. Single source of truth: the
   * diamond PDP reaches this through `theme.stones.getStone360()`, the
   * moissanite PDP renders `<video-360 shape="…">` directly.
   *
   * Shapes absent here simply get no spinner.
   *
   * @static {Object<string, string>}
   */
  static SHAPE_VIDEOS = {
    round: 'https://cdn.shopify.com/videos/c/o/v/1048dd645dd4465a81302bcf4ecb7270.mp4',
    princess: 'https://cdn.shopify.com/videos/c/o/v/59dbfba054924eb3922c0306a760b937.mp4',
    emerald: 'https://cdn.shopify.com/videos/c/o/v/0821f35376d0455290510be47afa1867.mp4',
    asscher: 'https://cdn.shopify.com/videos/c/o/v/cbe95538f30346dd9d9c3cce3bfaf942.mp4',
    cushion: 'https://cdn.shopify.com/videos/c/o/v/e326264f44994201aa3ffcdf9aa618b5.mp4',
    marquise: 'https://cdn.shopify.com/videos/c/o/v/ae4f7e61c11e42c085be31c956b16070.mp4',
    oval: 'https://cdn.shopify.com/videos/c/o/v/454f4ee3d9694b4783351335f9e9fede.mp4',
    radiant: 'https://cdn.shopify.com/videos/c/o/v/095df7c7ef614e60a7862422a032a32a.mp4',
    pear: 'https://cdn.shopify.com/videos/c/o/v/cb405687a3e3482997445a3a22342c50.mp4',
    heart: 'https://cdn.shopify.com/videos/c/o/v/32b3d671d0ad497491deb43300a2c37d.mp4'
  };

  /** @static {string} Where the source frame sequences live — used for poster images. */
  static FRAMES_BASE = 'https://images.allurez.com/gemsimages/magicvideos';

  /** @static {string} Pause glyph for the playback toggle. */
  static ICON_PAUSE = '<svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="2" y="1.5" width="3" height="9" rx="0.5"/><rect x="7" y="1.5" width="3" height="9" rx="0.5"/></svg>';

  /** @static {string} Play glyph for the playback toggle. */
  static ICON_PLAY = '<svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M3 1.5v9l7.5-4.5z"/></svg>';

  /**
   * Look up the video for a shape name, in any casing.
   *
   * @param {string} shape - e.g. "Emerald", "emerald"
   * @returns {string|null}
   */
  static srcForShape(shape) {
    return Video360.SHAPE_VIDEOS[String(shape || '').trim().toLowerCase()] || null;
  }

  /**
   * First frame of the sequence, used as the video's poster so the stone is
   * painted before the MP4 has buffered.
   *
   * @param {string} shape
   * @returns {string|null}
   */
  static posterForShape(shape) {

    const key = String(shape || '').trim().toLowerCase();

    if (!Video360.SHAPE_VIDEOS[key]) {
      return null;
    }

    return `${Video360.FRAMES_BASE}/${key}/${key}_01.jpg`;
  }


  /** @static {string} id of the shared toggle stylesheet, injected once per page. */
  static TOGGLE_STYLE_ID = 'stone-360-toggle-styles';

  /** @static {number} A press that travels less than this (px) is a tap, not a drag. */
  static TAP_MAX_PX = 6;

  /**
   * Inject the toggle styles once. Shared with the other 360 player — whichever
   * connects first wins, the other finds the tag already there.
   *
   * The button sits just below the stone, centred on the stage's bottom edge so
   * it never covers the diamond, hidden until the pointer hovers the stage (or
   * it receives keyboard focus), and only on laptop+ — on mobile the gallery
   * carousel handles the slide and there is no hover.
   */
  static ensureToggleStyles() {

    if (document.getElementById(this.TOGGLE_STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = this.TOGGLE_STYLE_ID;
    style.textContent = `
      .stone-360-toggle {
        position: absolute;
        bottom: 12px;
        left: 50%;
        width: 36px;
        height: 36px;
        margin: 0 0 0 -18px;
        display: none;
        align-items: center;
        justify-content: center;
        border: 0;
        padding: 0;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.92);
        color: #231F20;
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.15s ease;
        z-index: 10;
      }
      @media (min-width: 1024px) {
        .stone-360-toggle { display: flex; }
      }
      .stone-360-stage:hover .stone-360-toggle,
      .stone-360-toggle:focus-visible {
        opacity: 1;
      }
    `;

    document.head.appendChild(style);
  }

  constructor() {

    super();

    /** @private {number|null} Active drag pointer */
    this.pointerId = null;

    /** @private {number} Last pointer X during a drag */
    this.pointerX = 0;

    /** @private {boolean} Whether playback was running before the drag started */
    this.wasPlaying = true;

    /** @private {boolean} Whether the current press started on the toggle */
    this.pressedToggle = false;

    /** @private {number} X where the current press started, for tap detection */
    this.pressStartX = 0;

    /** @private {number} Furthest horizontal travel of the current press */
    this.pressTravel = 0;
  }

  connectedCallback() {

    const shape = this.getAttribute('shape');

    this.src = this.getAttribute('src') || Video360.srcForShape(shape);
    this.poster = this.getAttribute('poster') || Video360.posterForShape(shape) || '';
    this.label = this.getAttribute('label') || 'Stone 360 degree view';

    // No video for this shape — leave the page exactly as if we were never here.
    if (!this.src) {
      this.removeSlide();
      return;
    }

    this.render();
    this.initEvents();
  }

  disconnectedCallback() {
    this.video?.pause();
  }

  render() {

    // Opt the whole slide out of Swiper — every drag rotates the stone, and
    // the gallery's arrows page the carousel. Swiper resolves this class with
    // closest() from the event target, so it must sit on an ancestor of
    // everything draggable, not just the <video>.
    this.classList.add('swiper-no-swiping');

    Video360.ensureToggleStyles();

    this.innerHTML = `
      <div class="relative w-full select-none swiper-no-swiping stone-360-stage" data-stage>
        <video
          data-video
          class="w-full h-auto block swiper-no-swiping"
          src="${ this.src }"
          ${ this.poster ? `poster="${ this.poster }"` : '' }
          width="600"
          height="600"
          aria-label="${ this.label }"
          autoplay
          muted
          loop
          playsinline
          disablepictureinpicture
          preload="metadata"></video>

        <button
          type="button"
          data-toggle
          class="stone-360-toggle"
          aria-label="Pause 360° spin"></button>
      </div>
    `;

    this.stage = this.querySelector('[data-stage]');
    this.video = this.querySelector('[data-video]');
    this.toggleEl = this.querySelector('[data-toggle]');

    this.updateToggle();

    // Vertical page scrolling must still work when a touch starts on the video.
    this.stage.style.touchAction = 'pan-y';
    this.stage.style.cursor = 'w-resize';

    // Safari occasionally ignores the autoplay attribute on a freshly-built
    // element; play() is a no-op when it already started.
    const started = this.video.play();

    if (started && typeof started.catch === 'function') {
      started.catch(() => {});
    }
  }

  initEvents() {

    // A dead URL should leave the gallery as if the slide never existed.
    this.video.addEventListener('error', () => this.removeSlide(), { once: true });

    this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.stage.addEventListener('pointercancel', this.onPointerUp.bind(this));

    this.stage.addEventListener('dragstart', (event) => event.preventDefault());

    // Pointer presses on the toggle are resolved in onPointerUp (tap vs drag),
    // so the click handler only serves keyboard activation (detail === 0).
    this.toggleEl.addEventListener('click', (event) => {
      if (event.detail === 0) {
        this.toggle();
      }
    });
    this.video.addEventListener('play', () => this.updateToggle());
    this.video.addEventListener('pause', () => this.updateToggle());
  }

  /**
   * Flip between playing and paused from the playback toggle.
   *
   * @param {boolean} [playing] - State to flip from; defaults to the live one.
   *   A press pauses the video before it resolves into a tap, so the tap
   *   passes the state from before the press.
   */
  toggle(playing = !this.video.paused) {

    if (!playing) {
      const resumed = this.video.play();

      if (resumed && typeof resumed.catch === 'function') {
        resumed.catch(() => {});
      }
    } else {
      this.video.pause();
    }
  }

  /**
   * Reflect the playback state on the toggle button.
   * @private
   */
  updateToggle() {

    if (!this.toggleEl) {
      return;
    }

    const playing = !this.video.paused;

    this.toggleEl.innerHTML = playing ? Video360.ICON_PAUSE : Video360.ICON_PLAY;
    this.toggleEl.setAttribute('aria-label', playing ? 'Pause 360° spin' : 'Play 360° spin');
  }

  onPointerDown(event) {

    // A press on the toggle may still become a drag — the button sits where
    // people naturally grab the stone — so it is resolved on release.
    this.pressedToggle = Boolean(event.target.closest('[data-toggle]'));
    this.pressStartX = event.clientX;
    this.pressTravel = 0;

    // A drag is only meaningful once we know how long the video is; a press
    // on the toggle is still tracked so the tap can resolve.
    const canScrub = Number.isFinite(this.video.duration) && this.video.duration > 0;

    if (!canScrub && !this.pressedToggle) {
      return;
    }

    this.pointerId = event.pointerId;
    this.pointerX = event.clientX;
    this.wasPlaying = !this.video.paused;

    this.stage.setPointerCapture(event.pointerId);
    this.video.pause();
  }

  onPointerMove(event) {

    if (this.pointerId !== event.pointerId) {
      return;
    }

    this.pressTravel = Math.max(this.pressTravel, Math.abs(event.clientX - this.pressStartX));

    const width = this.stage.getBoundingClientRect().width;
    const duration = this.video.duration;

    if (!width || !Number.isFinite(duration) || duration <= 0) {
      return;
    }

    // A drag across the full width of the element is one full revolution.
    const delta = ((event.clientX - this.pointerX) / width) * duration;

    this.pointerX = event.clientX;

    // Wrap in both directions so the stone keeps turning past either end.
    this.video.currentTime = ((this.video.currentTime + delta) % duration + duration) % duration;
  }

  onPointerUp(event) {

    if (this.pointerId !== event.pointerId) {
      return;
    }

    if (this.stage.hasPointerCapture(event.pointerId)) {
      this.stage.releasePointerCapture(event.pointerId);
    }

    this.pointerId = null;

    // A press on the toggle that didn't travel is a tap: flip playback from
    // the state before the press (the press itself paused the video).
    if (this.pressedToggle && this.pressTravel < Video360.TAP_MAX_PX) {
      this.pressedToggle = false;
      this.toggle(this.wasPlaying);
      return;
    }

    this.pressedToggle = false;

    // Dragging resumes playback, matching <diamond-360>.
    if (this.wasPlaying) {
      const resumed = this.video.play();

      if (resumed && typeof resumed.catch === 'function') {
        resumed.catch(() => {});
      }
    }
  }

  /**
   * Take the viewer out of the page entirely — the gallery should fall back to
   * its other slides rather than show a broken player.
   *
   * @private
   */
  removeSlide() {
    (this.closest('.swiper-slide') || this).remove();
  }
}

customElements.define('video-360', Video360);
